import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import { CustomerService } from 'src/app/shareModule/services/customer.service';
import { SharedService } from 'src/app/shareModule/services/shared.service';

Chart.register(...registerables);

interface TagData {
  tag: string;
  count?: number;
  rank: number;
}

@Component({
  selector: 'app-tag-ranking-chart',
  templateUrl: './tag-ranking-chart.component.html',
  styleUrls: ['./tag-ranking-chart.component.scss'],
})
export class TagRankingChartComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @Input() selectedTags: any = {};
  @Input() tagCounts: number = 0;
  @Input() filter: string = '';
  @ViewChild('chartCanvas', { static: false })
  chartCanvas!: ElementRef<HTMLCanvasElement>;

  private chart: Chart<'bar'> | null = null;
  chartData: TagData[] = [];

  constructor(
    private customerService: CustomerService,
    private cdr: ChangeDetectorRef,
    private sharedService: SharedService
  ) {}

  ngOnInit(): void {
    this.prepareChartData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter'] || changes['selectedTags']) {
      if (changes['filter']) {
        this.loadTagRanking();
      } else {
        this.prepareChartData();
        this.initializeChart();
      }
    }
  }

  ngAfterViewInit(): void {
    this.initializeChart();
  }

  private initializeChart(): void {
    // Use setTimeout to ensure view is fully initialized
    setTimeout(() => {
      if (this.chartCanvas && this.chartData.length > 0) {
        this.createChart();
      }
    }, 0);
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
    }
  }

  loadTagRanking(): void {
    if (!this.filter) return;

    this.customerService
      .getMapTagsRank({
        filter: this.filter,
      })
      .subscribe((res: any) => {
        if (res.body) {
          this.selectedTags = res.body.tags || {};
          this.tagCounts = res.body.count || 0;
          this.prepareChartData();
          this.cdr.detectChanges();
          // Initialize or update chart after data is loaded and view is updated
          if (this.sharedService.isBrowser()) {
            if (this.chart) {
              this.updateChart();
            } else {
              this.initializeChart();
            }
          }
        }
      });
  }

  prepareChartData(): void {
    // Check if selectedTags is an object with tag names as keys
    if (
      !this.selectedTags ||
      typeof this.selectedTags !== 'object' ||
      Object.keys(this.selectedTags).length === 0
    ) {
      this.chartData = [];
      return;
    }

    // Convert object to array and sort by count (descending)
    const tagEntries = Object.entries(this.selectedTags).sort(
      (a, b) => (b[1] as number) - (a[1] as number)
    );

    // Prepare data with counts
    const dataWithCounts: TagData[] = tagEntries.map(([tag, count], index) => {
      return {
        tag: this.formatTagName(tag),
        count: count as number,
        rank: index + 1,
      };
    });

    this.chartData = dataWithCounts;
  }

  formatTagName(tag: string): string {
    if (!tag) return '';
    // Capitalize first letter and handle common formatting
    return tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase();
  }
  public get isMobile(): boolean {
    return this.sharedService.isBrowser() && window.innerWidth <= 767;
  }
  createChart(): void {
    if (!this.chartCanvas || this.chartData.length === 0) {
      return;
    }

    // Destroy existing chart if any
    if (this.chart) {
      this.chart.destroy();
    }

    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    if (!ctx) return;

    const labels = this.chartData.map((item) => item.tag);
    const dataValues = this.chartData.map((item) => item.count || item.rank);
    const backgroundColors = this.generateColors(this.chartData.length);

    const isMobile = this.isMobile;
    const barConfig = {
      barThickness: isMobile ? 10 : 18,
      barPercentage: isMobile ? 0.25 : 0.3,
      categoryPercentage: isMobile ? 0.8 : 0.7,
    };

    const yAxisFont = {
      size: isMobile ? 10 : 12,
      family: 'trebuchet',
      weight: 'bold' as const,
    };

    const config: ChartConfiguration<'bar'> = {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [
          {
            label: this.tagCounts > 0 ? 'Product Count' : 'Rank',
            data: dataValues,
            backgroundColor: backgroundColors,
            borderColor: backgroundColors.map((color) =>
              color.replace('0.8', '1')
            ),
            borderWidth: 1,
            borderRadius: 10,
            barThickness: barConfig.barThickness,
            barPercentage: barConfig.barPercentage,
            categoryPercentage: barConfig.categoryPercentage,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const item = this.chartData[context.dataIndex];
                if (this.tagCounts > 0) {
                  return `${item.tag}: ${item.count} products`;
                }
                return `${item.tag}: Rank #${item.rank}`;
              },
            },
          },
        },
        scales: {
          x: {
            beginAtZero: true,
            ticks: {
              stepSize: this.tagCounts > 0 ? undefined : 1,
              font: {
                size: 12,
              },
            },
            grid: { display: false },
          },
          y: {
            display: true,
            grid: { display: false },
            ticks: {
              font: yAxisFont,
              color: '#333',
              callback: (_value, index) => {
                const label = labels[index] ?? '';
                return label;
                // if (!isMobile) return label;
                // return label.length > 12 ? `${label.slice(0, 12)}…` : label;
              },
            },
          },
        },
      },
    };

    this.chart = new Chart(ctx, config);
  }

  updateChart(): void {
    if (this.chartData.length === 0) {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
      return;
    }

    if (!this.chart && this.chartCanvas) {
      this.createChart();
      return;
    }

    if (!this.chart) {
      return;
    }

    const labels = this.chartData.map((item) => item.tag);
    const dataValues = this.chartData.map((item) => item.count || item.rank);
    const backgroundColors = this.generateColors(this.chartData.length);

    this.chart.data.labels = labels;
    this.chart.data.datasets[0].data = dataValues;
    this.chart.data.datasets[0].backgroundColor = backgroundColors;
    this.chart.data.datasets[0].borderColor = backgroundColors.map((color) =>
      color.replace('0.8', '1')
    );

    this.chart.update();
  }

  generateColors(count: number): string[] {
    const colors = [
      'rgba(89, 40, 128, 0.8)', // Primary purple
      'rgba(138, 43, 226, 0.8)', // Blue violet
      'rgba(75, 0, 130, 0.8)', // Indigo
      'rgba(72, 61, 139, 0.8)', // Dark slate blue
      'rgba(106, 90, 205, 0.8)', // Slate blue
      'rgba(123, 104, 238, 0.8)', // Medium slate blue
      'rgba(147, 112, 219, 0.8)', // Medium purple
      'rgba(186, 85, 211, 0.8)', // Medium orchid
    ];

    // Repeat colors if needed
    const result: string[] = [];
    for (let i = 0; i < count; i++) {
      result.push(colors[i % colors.length]);
    }
    return result;
  }
}
